import Section from "@components/Chatbot/Summary/Section.tsx";
import React, {useEffect, useState} from "react";
import BarChartHighcharts from "@components/Charts/BarChart";
import {useSelector} from "react-redux";
import {mapDataForBarAndBubbleChart} from "@mappers/model/BarAndBubble";
import {mapDataForHighchartsGroupedBar} from "@mappers/model/GroupedBarChartMapper";
import {stackedBarChartDtoToModel} from "@mappers/model/StackedBarChartMapper";
import {getMetricsToRender, MAX_VELO_METRICS, TOTAL_DISTANCE_METRICS} from "@utils/chart_utils";
import {mapDataForHighcharts, mapDataForMaxVeloChart} from "@mappers/model/BarChartMapper";
import {getRestAndTravelDaysInChart} from "@components/Team/TeamAvgCharts";
import {DataColorUtil} from "@utils/color_utils";
import ChartErrorBoundary from "@components/Chatbot/Summary/ErrorBoundary";

const GPSSectionCharts = ({
    data_input,
}) => {
    const {
        info: { display_name: teamName, metric: teamMetric, restAndTravel, team_colors: teamColors, custom_charts: customChartData,
            acwr_threshold_config: acwrThresholdConfig
        },
        filter: { startDate, endDate, showWeekly, position, player_group, session },
        teamCharts,
        games,
        color_strategies: colorStrategies,
    } = useSelector(({ team }) => team);
    const [dataIn, setData] = React.useState(data_input);

    const [dataColorUtil, setDataColorUtil] = useState(null);

    useEffect(() => {
        if (data_input?.colors && data_input?.metrics?.length && colorStrategies?.length) {
            setDataColorUtil(new DataColorUtil({
                teamName,
                gpsBaselines: data_input?.colors || {}, // using colors as baselines
                teamGpsMetrics: data_input?.metrics || [],
                colorStrategies
            }));
        } else {
            setDataColorUtil(null);
        }
    }, [data_input, teamName, colorStrategies]);

    useEffect(() => {
        setData(data_input);
    }, [data_input]);

    const data = dataIn?.["data"];
    const restAndTravelDates = getRestAndTravelDaysInChart(
        startDate,
        endDate,
        data,
        restAndTravel,
        false,
        false
    );

    let charts = {};

    let key = "distance",
        maxValue = 0,
        maxPreValue = 0;
    let modifiedChartData = data?.map((item, index) => {
        let currVal = data[index][key];
        if (currVal > maxValue) maxValue = currVal;
        if (data[index][key + "_pre"] > maxPreValue)
            maxPreValue = data[index][key + "_pre"];
        if (index < 2) {
            return { ...item };
        } else {
            return {
                ...item,
                [key + "_rolling_average"]:
                    (currVal + data[index - 1][key] + data[index - 2][key]) / 3,
            };
        }
    });

    for (let i = 0; i < dataIn?.metrics?.length; i++) {
        let metric;
        let wellness;
        if (i < dataIn["metrics"].length) {
            metric = dataIn["metrics"][i];
            wellness = false;
        }

        let key = metric.key,
            maxValue = 0;

        data.map((item, index) => {
            let currVal = data[index][key];
            let per_minute_value = 0;
            if (currVal > maxValue) maxValue = currVal;

            if (item[key + "_per_minute"]) {
                per_minute_value = +item[key + "_per_minute"];
            } else {
                per_minute_value = (+item["total_time"] > 0 ? +(+item[key]/ +item["total_time"]).toFixed(2) : 0)
            }
            const itemWithPerMin = {
                ...item,
                [key + "_per_minute"]: per_minute_value,
            };

            dataIn.data[index][key + "_per_minute"] = per_minute_value;

            if (wellness === true || index < 2) {
                return { ...itemWithPerMin };
            } else {
                return {
                    ...itemWithPerMin,
                    [key + "_rolling_average"]:
                        (currVal +
                            data[index - 1][key] +
                            data[index - 2][key]) /
                        3,
                };
            }
        });
    }

    if (modifiedChartData?.length) {
        modifiedChartData.sort((a, b) => {
            return a.date < b.date ? -1 : 1;
        });
    }

    const getMappingFunction = (metric) => {
        let enableDrillDown = false;
        let customChart = null;
        let chartType = null;
        if(metric?.customChartData) {
            customChart = metric?.customChartData
            chartType = metric?.customChartData?.type
        }

        switch (chartType) {
            case 'bar_and_bubble_chart': {
                return mapDataForBarAndBubbleChart({
                    dataIn,
                    mainSeriesMetric: dataIn?.metrics.find( (metric) => metric.key === customChart.bar_metric),
                    secondarySeriesMetric: dataIn?.metrics.find( (metric) => metric.key === customChart.bubble_metric),
                    showACWR: true,
                    showRest: false,
                    showTravel: false,
                    restAndTravel,
                    startDate,
                    endDate,
                    teamName: teamName,
                    showPredictions: false,
                    games,
                    teamColors: teamColors.all,
                    showAvgPlotline: true,
                    position,
                    playerGroup: player_group,
                    session,
                    acwrThresholdConfig,
                    customTitle: customChart?.title,
                    enableDrillDown,
                    dataColorUtil
                });
            }
            case 'grouped_bar_chart': {
                return mapDataForHighchartsGroupedBar({
                    dataIn,
                    games,
                    metrics: dataIn?.metrics?.filter( (metric) => metric.key === customChart.bar1_metric || metric.key === customChart.bar2_metric),
                    teamColors: teamColors.all,
                    isComparison: false,
                    showACWR: true,
                    showRest: false,
                    showTravel: false,
                    restAndTravel,
                    startDate,
                    endDate,
                    showPredictions: false,
                    isTeamChart: false,
                    isMetricComparisonChart: true,
                    title: customChart?.title,
                    enableDrillDown,
                    dataColorUtil,
                    position,
                    playerGroup: player_group,
                    session,
                });
            }
            case 'stacked_bar_chart': {
                return stackedBarChartDtoToModel({
                    data: dataIn?.data,
                    showRest: false,
                    showTravel: false,
                    restAndTravel,
                    startDate,
                    endDate,
                    chartType: customChart?.stack_type,
                    showPredictions: false,
                    colors_100: dataIn?.colors,
                    games,
                    teamName,
                    position,
                    playerGroup: player_group,
                    session,
                    showACWR: customChart?.stack_type === 'distance' ? false : true,
                    customChartConfig: customChart,
                    metrics: dataIn?.metrics,
                    acwrThresholdConfig,
                    enableDrillDown
                });
            }
            default: {
                // custom chart type either not provided or not recognized.
                // Will get handled by the code below
            }
        }

        if(MAX_VELO_METRICS.concat(TOTAL_DISTANCE_METRICS).includes(metric.key)) {
            return mapDataForMaxVeloChart({
                dataIn,
                metric,
                showACWR: true,
                showRest: false,
                showTravel: false,
                restAndTravel,
                startDate,
                endDate,
                teamName: teamName,
                showPredictions: false,
                games,
                teamColors: teamColors.all,
                acwrThresholdConfig,
                enableDrillDown,
                dataColorUtil,
                position,
                playerGroup: player_group,
                session,
            })
        }

        return mapDataForHighcharts({
            dataIn,
            metric,
            showACWR: true,
            showRest: false,
            showTravel: false,
            restAndTravel,
            startDate,
            endDate,
            teamName,
            showPredictions: false,
            showAvgPlotline: true,
            games,
            teamColors: teamColors.all,
            session,
            playerGroup: player_group,
            position,
            teamMetric,
            acwrThresholdConfig,
            enableDrillDown,
            dataColorUtil
        });
    }

    const metricsToRender = getMetricsToRender(teamName, dataIn?.metrics, customChartData?.chart_types);

    const noGPSDataAvailable = () => {
        return !dataIn?.metrics?.length || !metricsToRender?.length || !dataIn?.data?.length;
    }

    const renderPrimaryCharts = () => {
        if (noGPSDataAvailable()) {
            return (
                <p style={{ textAlign: "center", color: "black", margin: 0, opacity: 0.4 }}>
                    No relevant GPS data available.
                </p>
            )
        }

        return metricsToRender.map((metric, index) => {
            const chartOptions = getMappingFunction(metric);
            if (!chartOptions) {
                console.warn(`Error generating ${metric} chart`)
                return null;
            }

            const mergedOptions = {
                ...chartOptions,
                chart: {
                    ...chartOptions.chart,
                    height: '100%'
                }
            };

            return (
                <li key={`chart-${metric?.key}${index}`} style={{ width: "48%" }}>
                    <ChartErrorBoundary
                        componentLabel={metric?.name || metric?.short || metric?.key || `Chart ${index + 1}`}
                        mergedOptions={mergedOptions}
                    >
                        <BarChartHighcharts
                            options={mergedOptions}
                        />
                    </ChartErrorBoundary>
                </li>
            );
        });
    };

    return (
        <div className={"chat-summary-charts-container"}>
            {renderPrimaryCharts()}
        </div>
    )
}

const DummyGPSSection = ({ summaryContent }) => {
    const gpsData = summaryContent?.gps;

    return (
        <Section
            title={"GPS"}
        >
            <GPSSectionCharts data_input={gpsData?.charts} />
        </Section>

    );
}

export default DummyGPSSection;